"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Container } from "@/components/ui/container";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Check,
  X,
  Star,
  Zap,
  Eye,
  Trophy,
  Users,
  MessageSquare,
  MapPin,
  TrendingUp,
  Camera,
  Crown,
} from "lucide-react";
import {
  SUBSCRIPTION_TIERS,
  FEATURES,
  FeatureTier,
  TIER_CONTENT_PERMISSIONS,
} from "@/types/subscription";
import { FeatureAccessService } from "@/lib/domain/core/services/feature-access.service";
import { useAuth } from "@/providers/auth-provider";
import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { routes } from "@/routes";

export function PricingSection() {
  const [isYearly, setIsYearly] = useState(false);

  const yearlyDiscount = 17; // ~2 months free

  return (
    <section
      id="pricing-section"
      className="py-20 px-4 bg-gradient-to-br from-background via-emerald-950/10 to-background"
    >
      <Container>
        {/* Header */}
        <div className="text-center mb-12 px-4 md:px-0">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Play More, Connect More
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8 px-2 sm:px-0">
            Start free with basic discovery. Upgrade to Member for full social
            access, messaging, and unlimited features.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <Label htmlFor="billing-toggle" className="text-sm">
              Monthly
            </Label>
            <Switch
              id="billing-toggle"
              checked={isYearly}
              onCheckedChange={setIsYearly}
            />
            <Label htmlFor="billing-toggle" className="text-sm">
              Yearly
              <Badge
                variant="secondary"
                className="ml-2 bg-emerald-500/20 text-emerald-400"
              >
                Save {yearlyDiscount}%
              </Badge>
            </Label>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 px-4 md:px-0">
          {SUBSCRIPTION_TIERS.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="w-full"
            >
              <PricingCard
                tier={tier}
                isYearly={isYearly}
                isPopular={tier.isPopular}
              />
            </motion.div>
          ))}
          {/* League Owner Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full"
          >
            <LeagueOwnerCard isYearly={isYearly} />
          </motion.div>
        </div>

        {/* What Can You Do Section */}
        <TierCapabilitiesSection />

        {/* Feature Comparison */}
        <FeatureComparisonTable />

        {/* FAQ */}
        <PricingFAQ />
      </Container>
    </section>
  );
}

function PricingCard({
  tier,
  isYearly,
  isPopular,
}: {
  tier: (typeof SUBSCRIPTION_TIERS)[0];
  isYearly: boolean;
  isPopular?: boolean;
}) {
  const router = useRouter();
  const { user, getIdToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const tierFeatures = FeatureAccessService.getTierFeatures(tier.id);
  const contentPerms = TIER_CONTENT_PERMISSIONS[tier.id];

  // Key selling points for each tier
  const keyPoints: Record<FeatureTier, string[]> = {
    free: [
      "Browse course information",
      "View course hours & contact",
      "Check course reviews",
      "1 challenge per week",
      "Join 1 league",
      "10-mile discovery radius",
    ],
    member: [
      "5 challenges per week",
      "Message other golfers",
      "Post to your profile",
      "Tag courses & clubs",
      "Unlimited leagues",
      "25-mile discovery radius",
      "Create & host lobbies",
      "Regional tournaments",
    ],
  };

  const handleSubscribe = async () => {
    // Free tier - just go to register or dashboard
    if (tier.id === "free") {
      if (user) {
        router.push("/local-links-v1");
      } else {
        router.push(routes.register);
      }
      return;
    }

    // Paid tier - logged in users go to checkout, others go to register
    if (!user) {
      // Save intended tier to redirect after registration
      router.push(
        `${routes.register}?tier=${tier.id}&interval=${
          isYearly ? "yearly" : "monthly"
        }`
      );
      return;
    }

    // Logged in user - call checkout API
    setIsLoading(true);
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication token not available");
      }

      const response = await fetch("/api/payments/checkout/subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tier: tier.id,
          interval: isYearly ? "yearly" : "monthly",
          successUrl: `${window.location.origin}/local-links-v1/settings?tab=billing&upgrade=success`,
          cancelUrl: window.location.href,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.error?.message || "Failed to create checkout session"
        );
      }

      // API wraps response in { success, data, meta } format
      const checkoutUrl = result.data?.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error("Checkout URL not found in response");
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      // Optionally show a toast here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card
      className={`relative h-full flex flex-col ${
        isPopular
          ? "border-emerald-500 shadow-lg shadow-emerald-500/20 scale-105"
          : "border-border hover:shadow-md hover:border-emerald-500/50"
      } transition-all duration-200`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-emerald-500 text-white px-3 py-1">
            <Star className="h-3 w-3 mr-1" />
            Most Popular
          </Badge>
        </div>
      )}

      <CardHeader className="text-center pb-4">
        <div className="mb-4">
          {tier.id === "free" && (
            <Eye className="h-12 w-12 text-slate-400 mx-auto" />
          )}
          {tier.id === "member" && (
            <Trophy className="h-12 w-12 text-emerald-500 mx-auto" />
          )}
        </div>

        <CardTitle className="text-2xl mb-2">{tier.name}</CardTitle>
        <p className="text-muted-foreground text-sm mb-4">{tier.description}</p>

        <div className="mb-2">
          {tier.id === "free" ? (
            <div className="text-3xl font-bold">Free</div>
          ) : (
            <div>
              <div className="text-3xl font-bold mb-1">
                $
                {isYearly
                  ? (tier.yearlyPrice / 100 / 12).toFixed(2)
                  : (tier.monthlyPrice / 100).toFixed(2)}
              </div>
              <p className="text-sm text-muted-foreground">
                per month{isYearly ? " (billed annually)" : ""}
              </p>
              {isYearly && (
                <p className="text-xs text-emerald-400 mt-1">
                  Save $
                  {((tier.monthlyPrice * 12 - tier.yearlyPrice) / 100).toFixed(
                    0
                  )}
                  /year
                </p>
              )}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        {/* Key Features */}
        <div className="space-y-2 flex-1">
          {keyPoints[tier.id].map((point, index) => (
            <div key={index} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span className="break-words">{point}</span>
            </div>
          ))}

          {/* Show what's NOT included for free tier */}
          {tier.id === "free" && (
            <>
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <X className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                <span>No messaging</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <X className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                <span>View content only (no posting)</span>
              </div>
            </>
          )}
        </div>

        {/* Content Permissions Summary */}
        <div className="border-t pt-4 mt-4 space-y-2">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Content Access:
          </div>
          <div className="flex flex-wrap gap-1">
            {contentPerms.canViewCommunityPosts ? (
              <Badge
                variant="outline"
                className="text-xs bg-emerald-500/10 border-emerald-500/30"
              >
                Social Feed
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs opacity-50">
                No Social Feed
              </Badge>
            )}
            {contentPerms.canPost ? (
              <Badge
                variant="outline"
                className="text-xs bg-emerald-500/10 border-emerald-500/30"
              >
                Can Post
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs opacity-50">
                Read Only
              </Badge>
            )}
            {contentPerms.canViewOutOfStateEvents && (
              <Badge
                variant="outline"
                className="text-xs bg-amber-500/10 border-amber-500/30"
              >
                Regional Events
              </Badge>
            )}
          </div>
        </div>

        {/* CTA Button */}
        <Button
          className={`w-full mt-6 ${
            isPopular ? "bg-emerald-500 hover:bg-emerald-600 text-white" : ""
          }`}
          variant={isPopular ? "default" : "outline"}
          size="lg"
          onClick={handleSubscribe}
          disabled={isLoading}
        >
          {isLoading ? "Processing..." : tier.ctaText}
        </Button>

        {tier.id === "member" && (
          <p className="text-xs text-center text-muted-foreground mt-2">
            14-day free trial • Cancel anytime
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function LeagueOwnerCard({ isYearly }: { isYearly: boolean }) {
  const router = useRouter();
  const { user, getIdToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // League Owner pricing
  const memberMonthly = 8.99;
  const memberYearly = 89;
  const leagueCreationFee = 4.99;
  const leagueMonthly = 9.99;

  // Calculate total first payment
  const memberCost = isYearly ? memberYearly : memberMonthly;
  const totalFirstMonth =
    leagueCreationFee +
    leagueMonthly +
    (isYearly ? memberYearly / 12 : memberMonthly);

  const keyPoints = [
    "Everything in Member tier",
    "Create & manage your league",
    "Custom branding & settings",
    "Member management tools",
    "Event scheduling & standings",
    "League analytics dashboard",
  ];

  const handleBundleCheckout = async () => {
    // Not logged in - go to register with bundle intent
    if (!user) {
      router.push(
        `${routes.register}?intent=league-owner&interval=${
          isYearly ? "yearly" : "monthly"
        }`
      );
      return;
    }

    setIsLoading(true);
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication token not available");
      }

      const response = await fetch("/api/payments/checkout/league-bundle", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          interval: isYearly ? "yearly" : "monthly",
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle redirect for existing members
        if (result.error?.details?.redirectUrl) {
          router.push(result.error.details.redirectUrl);
          return;
        }
        throw new Error(
          result.error?.message || "Failed to create checkout session"
        );
      }

      const checkoutUrl = result.data?.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error("Checkout URL not found");
      }
    } catch (error) {
      console.error("Error creating bundle checkout:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="relative h-full flex flex-col border-amber-500/50 shadow-lg shadow-amber-500/10 hover:shadow-xl hover:border-amber-500 transition-all duration-200">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <Badge className="bg-amber-500 text-white px-3 py-1">
          <Crown className="h-3 w-3 mr-1" />
          League Managers
        </Badge>
      </div>

      <CardHeader className="text-center pb-4">
        <div className="mb-4">
          <Crown className="h-12 w-12 text-amber-500 mx-auto" />
        </div>

        <CardTitle className="text-2xl mb-2">League Owner</CardTitle>
        <p className="text-muted-foreground text-sm mb-4">
          Already running a golf league? Bring it to Local Links
        </p>

        <div className="space-y-2">
          <div className="text-3xl font-bold">
            ${leagueCreationFee} + ${leagueMonthly}/mo
          </div>
          <p className="text-sm text-muted-foreground">
            League fees (creation + monthly)
          </p>
          <div className="bg-muted/50 rounded-lg p-2 text-xs">
            <span className="text-muted-foreground">Plus Member: </span>
            <span className="font-medium">
              ${isYearly ? (memberYearly / 12).toFixed(2) : memberMonthly}/mo
              {isYearly && " (billed annually)"}
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        <div className="space-y-2 flex-1">
          {keyPoints.map((point, index) => (
            <div key={index} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span className="break-words">{point}</span>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <Button
          className="w-full mt-6 bg-amber-500 hover:bg-amber-600 text-white"
          size="lg"
          onClick={handleBundleCheckout}
          disabled={isLoading}
        >
          {isLoading ? "Processing..." : "Start Your League"}
        </Button>

        <p className="text-xs text-center text-muted-foreground mt-2">
          7-day Member trial • League billed immediately
        </p>
      </CardContent>
    </Card>
  );
}

function TierCapabilitiesSection() {
  const capabilities = [
    {
      icon: Users,
      title: "Find Golfers",
      free: "10-mile radius",
      member: "25-mile radius",
    },
    {
      icon: Trophy,
      title: "Challenges",
      free: "1 per week",
      member: "5 per week",
    },
    {
      icon: MessageSquare,
      title: "Messaging",
      free: "Not available",
      member: "Message connections",
    },
    {
      icon: Camera,
      title: "Content Posting",
      free: "Not available",
      member: "10 posts/week",
    },
    {
      icon: MapPin,
      title: "Tournament Search",
      free: "Local only",
      member: "Regional + Out-of-state",
    },
    {
      icon: TrendingUp,
      title: "Leagues",
      free: "Join 1 league",
      member: "Unlimited + create",
    },
  ];

  return (
    <div className="mb-16 px-4 md:px-0">
      <h3 className="text-2xl font-bold text-center mb-8">What Can You Do?</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {capabilities.map((cap, index) => (
          <motion.div
            key={cap.title}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <Card className="bg-card/50">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <cap.icon className="h-5 w-5 text-emerald-500" />
                  <h4 className="font-semibold">{cap.title}</h4>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Free:</span>
                    <span
                      className={
                        cap.free === "Not available" ? "text-red-400" : ""
                      }
                    >
                      {cap.free}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Member:</span>
                    <span className="text-emerald-400">{cap.member}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function FeatureComparisonTable() {
  return (
    <div className="mb-16 px-4 md:px-0">
      <h3 className="text-2xl font-bold text-center mb-8">
        Complete Feature Comparison
      </h3>

      <div className="hidden md:block">
        <DesktopFeatureComparison />
      </div>
      <div className="md:hidden">
        <MobileFeatureComparison />
      </div>
    </div>
  );
}

function DesktopFeatureComparison() {
  const categories = FeatureAccessService.getAllFeaturesByCategory();

  return (
    <div className="bg-card border rounded-lg overflow-hidden">
      <div className="overflow-x-auto -mx-4 md:mx-0">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="text-left p-4 font-semibold">Features</th>
              {SUBSCRIPTION_TIERS.map((tier) => (
                <th
                  key={tier.id}
                  className="text-center p-4 font-semibold min-w-[120px]"
                >
                  <span
                    className={tier.id === "member" ? "text-emerald-400" : ""}
                  >
                    {tier.name}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(categories).map(
              ([categoryName, features]) =>
                features.length > 0 && (
                  <React.Fragment key={categoryName}>
                    <tr className="border-b bg-muted/10">
                      <td className="p-4 font-semibold text-sm uppercase text-muted-foreground capitalize">
                        {categoryName}
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                    {features.map((feature) => (
                      <tr
                        key={feature.id}
                        className="border-b hover:bg-muted/20"
                      >
                        <td className="p-4">
                          <div className="font-medium">{feature.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {feature.description}
                          </div>
                        </td>
                        {SUBSCRIPTION_TIERS.map((tier) => (
                          <td key={tier.id} className="p-4 text-center">
                            <FeatureCell feature={feature} tier={tier.id} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MobileFeatureComparison() {
  const categories = FeatureAccessService.getAllFeaturesByCategory();

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full bg-card border rounded-lg"
    >
      {Object.entries(categories).map(
        ([categoryName, features]) =>
          features.length > 0 && (
            <AccordionItem key={categoryName} value={categoryName}>
              <AccordionTrigger className="px-4 hover:no-underline">
                <span className="font-semibold text-sm uppercase text-muted-foreground capitalize">
                  {categoryName}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="divide-y">
                  {features.map((feature) => (
                    <div key={feature.id} className="p-4 space-y-3">
                      <div>
                        <div className="font-medium">{feature.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {feature.description}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 bg-muted/10 p-3 rounded-md">
                        {SUBSCRIPTION_TIERS.map((tier) => (
                          <div key={tier.id} className="text-center space-y-2">
                            <div
                              className={`text-[10px] uppercase font-bold ${
                                tier.id === "member"
                                  ? "text-emerald-400"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {tier.name}
                            </div>
                            <div className="flex justify-center items-center h-full">
                              <FeatureCell feature={feature} tier={tier.id} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )
      )}
    </Accordion>
  );
}

function FeatureCell({
  feature,
  tier,
}: {
  feature: (typeof FEATURES)[0];
  tier: FeatureTier;
}) {
  const limit = feature.limits[tier];

  if (limit.limit === 0) {
    return <X className="h-4 w-4 text-red-400 mx-auto" />;
  }

  if (limit.limit === undefined) {
    return (
      <Check
        className={`h-4 w-4 mx-auto ${
          tier === "member" ? "text-emerald-500" : "text-green-500"
        }`}
      />
    );
  }

  return (
    <div className="text-sm">
      <Check
        className={`h-4 w-4 mx-auto mb-1 ${
          tier === "member" ? "text-emerald-500" : "text-green-500"
        }`}
      />
      <div className="text-xs text-muted-foreground">{limit.description}</div>
    </div>
  );
}

function PricingFAQ() {
  const faqs = [
    {
      question: "What's included in the Free plan?",
      answer:
        "The Free plan lets you browse course information including hours, contact details, and reviews. You can also send 1 challenge per week, join 1 league, and discover golfers within 10 miles.",
    },
    {
      question: "What can I do as a Member?",
      answer:
        "Member subscribers can send up to 5 challenges per week, message other golfers, post content to their profile, tag courses and clubs, join unlimited leagues, create lobbies, and access regional tournaments. It's everything you need to connect and compete with the local golf community.",
    },
    {
      question: "How does posting and tagging work?",
      answer:
        "Member subscribers can post content to their profile and tag courses, clubs, and leagues. When you tag an entity, they can approve your post to appear on their feed - creating a social network around golf venues and communities.",
    },
    {
      question: "Can I upgrade or downgrade at any time?",
      answer:
        "Yes! You can upgrade, downgrade, or cancel your subscription at any time. Changes take effect at your next billing cycle. We also offer a 14-day free trial for Member subscriptions.",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "We accept all major credit cards, debit cards, and ACH bank transfers through our secure Stripe integration.",
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-0">
      <h3 className="text-2xl font-bold text-center mb-8">
        Frequently Asked Questions
      </h3>

      <div className="space-y-6">
        {faqs.map((faq, index) => (
          <div key={index} className="border-b pb-4">
            <h4 className="font-semibold mb-2">{faq.question}</h4>
            <p className="text-muted-foreground">{faq.answer}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
